/**
 * Renders a markdown legal/content document (privacy, terms, etc.)
 */

import { ContentBlock, Box } from 'braid-design-system';
import React from 'react';
import { Link } from 'react-router';

import { PageHead } from '../PageHead/PageHead';

import {
  firstBodyParagraph,
  parseMarkdownDoc,
  type Block,
  type InlineNode,
} from './parseMarkdown';

import * as styles from './MarkdownDoc.css';

interface MarkdownDocPageProps {
  markdown: string;
  path: string;
  pageTitle: string;
}

const renderInline = (nodes: InlineNode[]) =>
  nodes.map((node, index) => {
    if (node.type === 'strong') {
      return <strong key={index}>{node.value}</strong>;
    }

    if (node.type === 'link') {
      if (node.href.startsWith('/')) {
        return (
          <Link key={index} to={node.href} className={styles.inlineLink}>
            {node.value}
          </Link>
        );
      }

      return (
        <a key={index} href={node.href} className={styles.inlineLink}>
          {node.value}
        </a>
      );
    }

    return <React.Fragment key={index}>{node.value}</React.Fragment>;
  });

const isLeadParagraph = (block: Block, index: number) =>
  index === 0 &&
  block.type === 'paragraph' &&
  block.children.length === 1 &&
  block.children[0].type === 'strong';

const renderBlocks = (blocks: Block[]) =>
  blocks.map((block, index) => {
    if (block.type === 'rule') {
      return <hr key={index} className={styles.rule} />;
    }

    if (block.type === 'list') {
      return (
        <ul key={index} className={styles.list}>
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex} className={styles.listItem}>
              {renderInline(item)}
            </li>
          ))}
        </ul>
      );
    }

    if (block.type === 'heading') {
      return (
        <h2 key={index} className={styles.sectionTitle}>
          {renderInline(block.children)}
        </h2>
      );
    }

    return (
      <p
        key={index}
        className={isLeadParagraph(block, index) ? styles.lead : styles.body}
      >
        {renderInline(block.children)}
      </p>
    );
  });

export const MarkdownDocPage: React.FC<MarkdownDocPageProps> = ({
  markdown,
  path,
  pageTitle,
}) => {
  const doc = parseMarkdownDoc(markdown);

  return (
    <ContentBlock width="large">
      <PageHead
        title={pageTitle}
        description={firstBodyParagraph(doc)}
        path={path}
      />
      <Box paddingY="large">
        <article className={styles.container}>
          <h1 className={styles.heading}>{doc.title || pageTitle}</h1>
          {renderBlocks(doc.intro)}
          {doc.sections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className={styles.section}
            >
              <h2 className={styles.sectionTitle}>
                {renderInline(section.heading)}
              </h2>
              {renderBlocks(section.blocks)}
            </section>
          ))}
        </article>
      </Box>
    </ContentBlock>
  );
};
